import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputBase,
  LinearProgress,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TableChartIcon from "@mui/icons-material/TableChart";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CommentIcon from "@mui/icons-material/AddComment";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BookIcon from "@mui/icons-material/Book";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import {
  addCommentToMultipleWnioski,
  fetchWnioskiWithHistory,
  deleteWniosek,
  fetchPowodWpisaniaOptions,
  fetchUsers,
} from "./RejestrFunctions";
import { COLORS } from "../UserDashboard/dashboardConfig";
import { usePageStyles } from "../common/pageStyles";
import { useSession } from "../common/useSession";
import { useViewPreferences } from "../common/useViewPreferences";
import FilterDrawer, { FilterCard, countFilled } from "../common/FilterDrawer";
import ColumnsMenu from "../common/ColumnsMenu";

const VIEW = "rejestrWnioskow";
const FILTERS_STORAGE_KEY = "filtersRejestr";
const COLUMNS_STORAGE_KEY = "columnsView.rejestrWnioskow";
const PAGE_SIZE_OPTIONS = [20, 50, 100];

const ALLOWED_USERS = new Set([
  "molszewska",
  "ahrechaniuk",
  "mjakubowska",
  "adobrowolska",
]);

const STATUS_OPTIONS = [
  "W toku",
  "Do weryfikacji",
  "Do poprawy",
  "Do wysłania",
  "Wysłany",
  "Do poprawy na wniosek UC",
  "Wycofany",
  "Odebrany z urzędu",
  "Zakończony",
];

const IN_PROGRESS_STATUSES = [
  "W toku",
  "Do wysłania",
  "Do weryfikacji",
  "Do poprawy",
  "Do poprawy na wniosek UC",
];

const PROCEDURE_OPTIONS = ["1000", "4000", "4071", "4500", "6110", "6121"];
const PAYER_OPTIONS = ["KLIENT", "WEGA"];

const STATUS_TONES = {
  "W toku": { bg: "#fff8e1", fg: "#b26a00", dot: "#fdd835" },
  "Do weryfikacji": { bg: "#f3e5f5", fg: "#7b1fa2", dot: "#ab47bc" },
  "Do poprawy": { bg: "#fff3e0", fg: "#e65100", dot: "#fb8c00" },
  "Do poprawy na wniosek UC": { bg: "#fff3e0", fg: "#bf360c", dot: "#f4511e" },
  "Do wysłania": { bg: "#e3f2fd", fg: "#1565c0", dot: "#42a5f5" },
  Wysłany: { bg: "#ffebee", fg: "#c62828", dot: "#e53935" },
  Wycofany: { bg: "#eeeeee", fg: "#616161", dot: "#9e9e9e" },
  "Odebrany z urzędu": { bg: "#e0f2f1", fg: "#00695c", dot: "#26a69a" },
  Zakończony: { bg: "#eeeeee", fg: "#424242", dot: "#bdbdbd" },
};

const getStatusTone = (status) =>
  STATUS_TONES[status] || { bg: "#f5f5f5", fg: "#424242", dot: "#bdbdbd" };

// Kolumny w menu "Kolumny". Nr wniosku (pierwsza, lepka kolumna) jest zawsze widoczny.
// sortable - sortowanie po stronie przeglądarki (wszystkie dane są już załadowane).
const COLUMNS = [
  { id: "wyslano_przypomnienie", label: "Znaczniki", width: 110 },
  { id: "status", label: "Status", width: 190, sortable: true },
  { id: "data_terminu_odpowiedzi", label: "Termin ustawowy wezwania z urzędu", width: 190, sortable: true },
  { id: "data_pytania_z_urzedu", label: "Data pytania z urzędu", width: 160, sortable: true },
  { id: "data_odpowiedzi_na_pytanie", label: "Data naszej odpowiedzi", width: 160, sortable: true },
  { id: "updated_at", label: "Przypomnienie dla kontrahenta", width: 190, sortable: true },
  { id: "podpisany", label: "Osoba odpowiedzialna", width: 170, sortable: true },
  { id: "data_wpisania", label: "Data wpisania", width: 160, sortable: true },
  { id: "powod_wpisania", label: "Powód wpisania", width: 160, sortable: true },
  { id: "sciezkaDoPliku", label: "Ścieżka do pliku", width: 220 },
  { id: "data_SAD", label: "Data SAD", width: 120, sortable: true },
  { id: "nr_MRN", label: "Nr MRN", width: 210, sortable: true, noTruncate: true },
  { id: "nr_akt", label: "Nr akt", width: 130, sortable: true },
  { id: "nadawca_odbiorca", label: "Nadawca", width: 190, sortable: true },
  { id: "odbiorca", label: "Odbiorca", width: 190, sortable: true },
  { id: "rodzaj_SAD", label: "Rodzaj SAD", width: 130, sortable: true },
  { id: "procedura", label: "Procedura", width: 110, sortable: true },
  { id: "platnik", label: "Płatnik", width: 110, sortable: true },
  { id: "data_wyslania", label: "Wysłano do UC", width: 140, sortable: true },
  { id: "data_odebrania", label: "Odebrano decyzję z UC", width: 170, sortable: true },
  { id: "utworzony_przez", label: "Utworzony przez", width: 150, sortable: true },
];

const DEFAULT_HIDDEN_COLUMNS = [];

// Minimalna szerokość lepkiej kolumny z numerem wniosku; dłuższy numer poszerza kolumnę (nie jest ucinany).
const STICKY_NUMBER_WIDTH = 160;

// W widoku wezwań te kolumny nie mają sensu.
const WEZWANIA_HIDDEN = [
  "data_SAD",
  "rodzaj_SAD",
  "platnik",
  "data_wyslania",
  "data_odebrania",
];

const DATE_ONLY_COLUMNS = [
  "data_SAD",
  "data_wyslania",
  "data_odebrania",
  "data_terminu_odpowiedzi",
  "data_pytania_z_urzedu",
  "data_odpowiedzi_na_pytanie",
];
const DATE_TIME_COLUMNS = ["data_wpisania", "updated_at"];

// Mapowanie nazw pól na ładniejsze etykiety (historia zmian)
const fieldLabels = {
  powod_wpisania: "Powód wpisania",
  nr_MRN: "Nr MRN",
  nr_akt: "Numer akt",
  nadawca_odbiorca: "Nadawca",
  odbiorca: "Odbiorca",
  podpisany: "Osoba odpowiedzialna",
  rodzaj_SAD: "Rodzaj SAD",
  procedura: "Procedura",
  data_SAD: "Data SAD",
  data_wyslania: "Data wysłania",
  platnik: "Płatnik",
  data_odebrania: "Data odebrania decyzji z UC",
  sciezkaDoPliku: "Ścieżka",
  data_doreczenia: "Data doręczenia do Kontrahenta",
  doreczenie_nie_dotyczy: "Doręczenie nie dotyczy",
  powod_braku_doreczenia: "Powód braku daty doręczenia",
  status: "Status",
  pytanie_z_urzedu: "Pytanie z urzędu",
  odpowiedz_na_pytanie: "Odpowiedź na pytanie",
  termin_ustawowy_odpowiedzi: "Termin ustawowy na odpowiedź",
  data_terminu_odpowiedzi: "Data terminu ustawowego",
  data_pytania_z_urzedu: "Data pytania z urzędu",
  data_odpowiedzi_na_pytanie: "Data naszej odpowiedzi",
  wyslano_przypomnienie: "Wysłano przypomnienie",
  updated_at: "Data wysłania przypomnienia",
  data_wpisania: "Data wpisania",
  unique_number: "Numer wniosku",
  utworzony_przez: "Utworzony przez",
  kontrolujacy: "Kontrolujący",
  data_zakonczenia: "Data zakończenia",
  weryfikacja: "Weryfikacja",
  duplikat: "Duplikat",
};

const EMPTY_FILTERS = {
  nr_MRN: "",
  nr_akt: "",
  data_wpisania: "",
  status: [],
  podpisany: "",
  procedura: "",
  platnik: "",
  data_wyslania_start: "",
  data_wyslania_end: "",
  data_odebrania_start: "",
  data_odebrania_end: "",
  searchTerm: "",
  powod_wpisania: "",
  odbiorca: "",
  nadawca: "",
  data_terminu_start: "",
  data_terminu_end: "",
  pokazBiezacePytania: false,
  pokazBezOdpowiedzi: false,
};

// Zapisane filtry mogą pochodzić ze starej wersji (status jako tekst, dodatkowe pola).
const loadFilters = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) || "null");
    if (!stored || typeof stored !== "object") return EMPTY_FILTERS;
    const merged = { ...EMPTY_FILTERS, ...stored };
    if (!Array.isArray(merged.status)) {
      merged.status = merged.status ? [merged.status] : [];
    }
    return merged;
  } catch (e) {
    return EMPTY_FILTERS;
  }
};

const sameSet = (a = [], b = []) =>
  a.length === b.length && a.every((v) => b.includes(v));

const isWezwanie = (row) =>
  (row.powod_wpisania || "").toString().trim().toUpperCase() === "WEZWANIE";

// Jedyna funkcja filtrująca - zawiera WSZYSTKIE kryteria.
const day = (value) => moment(value).format("YYYY-MM-DD");

const applyFilters = (data, f, isWezwaniaMode = false) => {
  const searchLower = (f.searchTerm || "").toLowerCase();
  return data.filter((row) => {
    const isInCurrentViewScope = isWezwaniaMode
      ? isWezwanie(row)
      : !isWezwanie(row);
    const matchesSearchTerm =
      searchLower === "" ||
      Object.values(row).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          typeof value !== "object" &&
          value.toString().toLowerCase().includes(searchLower),
      );

    return (
      matchesSearchTerm &&
      (f.nr_MRN === "" ||
        (row.nr_MRN != null && row.nr_MRN.includes(f.nr_MRN))) &&
      (f.powod_wpisania === "" ||
        (row.powod_wpisania && row.powod_wpisania.includes(f.powod_wpisania))) &&
      (f.nr_akt === "" ||
        (row.nr_akt != null && row.nr_akt.includes(f.nr_akt))) &&
      (f.nadawca === "" ||
        (row.nadawca_odbiorca &&
          row.nadawca_odbiorca.toLowerCase().includes(f.nadawca.toLowerCase()))) &&
      (f.odbiorca === "" ||
        (row.odbiorca &&
          row.odbiorca.toLowerCase().includes(f.odbiorca.toLowerCase()))) &&
      (f.data_wpisania === "" ||
        day(row.data_wpisania) === moment(f.data_wpisania).format("YYYY-MM-DD")) &&
      (f.status.length === 0 || f.status.includes(row.status)) &&
      (f.podpisany === "" || row.podpisany === f.podpisany) &&
      (f.procedura === "" || row.procedura === f.procedura) &&
      (f.platnik === "" || row.platnik === f.platnik) &&
      (f.data_wyslania_start === "" ||
        moment(day(row.data_wyslania)).isSameOrAfter(f.data_wyslania_start)) &&
      (f.data_wyslania_end === "" ||
        moment(day(row.data_wyslania)).isSameOrBefore(f.data_wyslania_end)) &&
      (f.data_odebrania_start === "" ||
        moment(day(row.data_odebrania)).isSameOrAfter(f.data_odebrania_start)) &&
      (f.data_odebrania_end === "" ||
        moment(day(row.data_odebrania)).isSameOrBefore(f.data_odebrania_end)) &&
      (f.data_terminu_start === "" ||
        moment(day(row.data_terminu_odpowiedzi)).isSameOrAfter(f.data_terminu_start)) &&
      (f.data_terminu_end === "" ||
        moment(day(row.data_terminu_odpowiedzi)).isSameOrBefore(f.data_terminu_end)) &&
      (!f.pokazBiezacePytania ||
        (isInCurrentViewScope &&
          ((row.data_pytania_z_urzedu !== null &&
            row.data_pytania_z_urzedu !== "") ||
            row.termin_ustawowy_odpowiedzi === 1) &&
          row.status !== "Zakończony" &&
          row.status !== "Wycofany")) &&
      (!f.pokazBezOdpowiedzi ||
        (isInCurrentViewScope &&
          row.data_terminu_odpowiedzi !== null &&
          row.data_terminu_odpowiedzi !== "" &&
          (row.data_odpowiedzi_na_pytanie === null ||
            row.data_odpowiedzi_na_pytanie === "")))
    );
  });
};

const fmtDay = (value) => moment(value, "YYYY-MM-DD").format("DD.MM.YYYY");

const rangeLabel = (name, from, to) => {
  if (from && to) return `${name}: ${fmtDay(from)} – ${fmtDay(to)}`;
  if (from) return `${name}: od ${fmtDay(from)}`;
  return `${name}: do ${fmtDay(to)}`;
};

// Chipy aktywnych filtrów; `clear` to fragment filtrów, który po scaleniu go usuwa.
const describeFilters = (f) => {
  const chips = [];
  const text = (key, label) => {
    if (f[key]) chips.push({ id: key, label: `${label}: ${f[key]}`, clear: { [key]: "" } });
  };
  const range = (fromKey, toKey, label) => {
    if (f[fromKey] || f[toKey]) {
      chips.push({
        id: fromKey,
        label: rangeLabel(label, f[fromKey], f[toKey]),
        clear: { [fromKey]: "", [toKey]: "" },
      });
    }
  };
  text("searchTerm", "Szukaj");
  text("nr_MRN", "MRN");
  text("nr_akt", "Nr akt");
  text("nadawca", "Nadawca");
  text("odbiorca", "Odbiorca");
  if (f.data_wpisania) {
    chips.push({
      id: "data_wpisania",
      label: `Wpisano: ${fmtDay(f.data_wpisania)}`,
      clear: { data_wpisania: "" },
    });
  }
  if (f.status.length > 0) {
    const shown = f.status.length <= 2
      ? f.status.join(", ")
      : `${f.status.slice(0, 2).join(", ")} +${f.status.length - 2}`;
    chips.push({ id: "status", label: `Status: ${shown}`, clear: { status: [] } });
  }
  text("podpisany", "Odpowiedzialny");
  text("procedura", "Procedura");
  text("powod_wpisania", "Powód");
  text("platnik", "Płatnik");
  range("data_wyslania_start", "data_wyslania_end", "Wysłano do UC");
  range("data_odebrania_start", "data_odebrania_end", "Odebrano z UC");
  range("data_terminu_start", "data_terminu_end", "Termin odpowiedzi");
  if (f.pokazBiezacePytania) {
    chips.push({
      id: "pokazBiezacePytania",
      label: "Bieżące pytania z urzędu",
      clear: { pokazBiezacePytania: false },
    });
  }
  if (f.pokazBezOdpowiedzi) {
    chips.push({
      id: "pokazBezOdpowiedzi",
      label: "Wezwania bez naszej odpowiedzi",
      clear: { pokazBezOdpowiedzi: false },
    });
  }
  return chips;
};

const formatValue = (value) =>
  moment(value, moment.ISO_8601, true).isValid()
    ? moment(value).format("DD-MM-YYYY")
    : value;

const compareValues = (a, b, dir) => {
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1; // puste zawsze na końcu
  if (bEmpty) return -1;
  const result =
    typeof a === "number" && typeof b === "number"
      ? a - b
      : String(a).localeCompare(String(b), "pl", { numeric: true, sensitivity: "base" });
  return dir === "desc" ? -result : result;
};

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const sortUsers = (list) =>
  [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pl"));

const WniosekRow = React.memo(
  ({ row, columns, selected, onSelect, expanded, onToggle, onEdit, onDelete, isAdmin, wezwaniaMode }) => {
    const classes = usePageStyles();

    const renderCell = (id) => {
      const value = row[id];
      if (id === "wyslano_przypomnienie") {
        const reminderSent = Number(row.wyslano_przypomnienie) === 1;
        const received = Number(row.termin_ustawowy_odpowiedzi) === 1;
        if (!reminderSent && !received) return <span className={classes.muted}>—</span>;
        return (
          <span className={classes.inline}>
            {reminderSent && (
              <Tooltip title="Przypomnienie wysłane" arrow>
                <BookIcon style={{ color: COLORS.success, fontSize: 22 }} />
              </Tooltip>
            )}
            {received && (
              <Tooltip title="Otrzymano wezwanie" arrow>
                <AccessTimeIcon style={{ color: COLORS.primary, fontSize: 22 }} />
              </Tooltip>
            )}
          </span>
        );
      }
      if (id === "status") {
        if (!value) return <span className={classes.muted}>—</span>;
        const tone = getStatusTone(value);
        return (
          <span className={classes.pill} style={{ background: tone.bg, color: tone.fg }}>
            <span className={classes.dot} style={{ background: tone.dot }} />
            {value}
          </span>
        );
      }
      if (id === "nr_MRN") {
        return (
          <span className={classes.inlineNoWrap}>
            {row.duplikat === 1 && (
              <Tooltip title="DUPLIKAT" arrow>
                <span
                  className={classes.pill}
                  style={{ background: "#e8f5e9", color: "#1b5e20", padding: "1px 8px" }}
                >
                  Duplikat
                </span>
              </Tooltip>
            )}
            {value ? (
              <span style={{ whiteSpace: "nowrap" }}>{value}</span>
            ) : (
              <span className={classes.muted}>—</span>
            )}
          </span>
        );
      }
      let shown = value;
      if (shown && DATE_ONLY_COLUMNS.includes(id)) shown = moment(shown).format("DD-MM-YYYY");
      else if (shown && DATE_TIME_COLUMNS.includes(id)) {
        shown = moment(shown).format("DD-MM-YYYY, HH:mm");
      }
      if (shown === null || shown === undefined || shown === "") {
        return <span className={classes.muted}>—</span>;
      }
      return (
        <Tooltip title={String(shown)} enterDelay={600}>
          <span className={classes.ellipsis}>{shown}</span>
        </Tooltip>
      );
    };

    return (
      <>
        <TableRow hover={false} className={`${classes.row} ${selected ? classes.rowSelected : ""}`}>
          <TableCell
            padding="checkbox"
            className={classes.stickyCell}
            style={{ left: 0, width: 52, minWidth: 52 }}
          >
            <Checkbox
              size="small"
              color="primary"
              checked={selected}
              onChange={() => onSelect(row)}
              inputProps={{ "aria-label": `Wybierz wniosek ${row.unique_number}` }}
            />
          </TableCell>
          <TableCell
            padding="none"
            className={classes.stickyCell}
            style={{ left: 52, width: 40, minWidth: 40 }}
          >
            <Tooltip title="Historia zmian i komentarze">
              <IconButton size="small" onClick={() => onToggle(row.id)} aria-label="Pokaż historię">
                {expanded ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell
            className={`${classes.stickyCell} ${classes.stickyEdge}`}
            style={{ left: 92, minWidth: STICKY_NUMBER_WIDTH, whiteSpace: "nowrap" }}
          >
            <button type="button" className={classes.link} onClick={() => onEdit(row.id)}>
              {row.unique_number || "—"}
            </button>
          </TableCell>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              style={{ minWidth: column.width, maxWidth: column.noTruncate ? undefined : column.width }}
            >
              {renderCell(column.id)}
            </TableCell>
          ))}
          <TableCell style={{ width: 96, minWidth: 96 }}>
            <div className={classes.actions}>
              <Tooltip title={wezwaniaMode ? "Edytuj wezwanie" : "Edytuj wniosek"}>
                <IconButton size="small" onClick={() => onEdit(row.id)} aria-label="Edytuj">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Usuń wniosek">
                  <IconButton size="small" onClick={() => onDelete(row)} aria-label="Usuń wniosek">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            colSpan={columns.length + 4}
            className={`${classes.detailCell} ${expanded ? classes.detailCellOpen : ""}`}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <div className={classes.detailWrap}>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Historia zmian we wniosku</div>
                  <div className={classes.detailList}>
                    {!row.history || row.history.length === 0 ? (
                      <div className={classes.detailEmpty}>Brak zmian.</div>
                    ) : (
                      row.history.map((change) => (
                        <div key={change.id} className={classes.detailItem}>
                          <strong>{fieldLabels[change.field] || change.field}</strong>
                          <div className={classes.detailChange}>
                            <span style={{ color: COLORS.danger }}>
                              {String(formatValue(change.old_value) ?? "—")}
                            </span>
                            {" → "}
                            <span style={{ color: COLORS.success, fontWeight: 600 }}>
                              {String(formatValue(change.new_value) ?? "—")}
                            </span>
                          </div>
                          <div className={classes.detailMeta}>
                            {change.username} ·{" "}
                            {moment(change.timestamp).format("DD-MM-YYYY, HH:mm")}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Komentarze</div>
                  <div className={classes.detailList}>
                    {!row.comments || row.comments.length === 0 ? (
                      <div className={classes.detailEmpty}>Brak komentarzy.</div>
                    ) : (
                      row.comments.map((comment) => (
                        <div key={comment.id} className={classes.detailItem}>
                          <div>{comment.comment}</div>
                          <div className={classes.detailMeta}>
                            {comment.username} ·{" "}
                            {moment(comment.created_at).format("DD-MM-YYYY, HH:mm")}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  },
);

const RejestrWnioskow = () => {
  const classes = usePageStyles();
  const navigate = useNavigate();
  const { username, isAdmin } = useSession();
  const canSeeLists = isAdmin || ALLOWED_USERS.has((username || "").toLowerCase());

  const prefs = useViewPreferences({
    view: VIEW,
    storageKey: COLUMNS_STORAGE_KEY,
    defaultHiddenColumns: DEFAULT_HIDDEN_COLUMNS,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: 20,
  });

  const [wnioski, setWnioski] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [powodOptions, setPowodOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);

  const [filters, setFilters] = useState(loadFilters);
  const [wezwaniaMode, setWezwaniaMode] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(0);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [exportAnchor, setExportAnchor] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const searchRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWnioskiWithHistory();
      setWnioski(response.data);
      setError(null);
    } catch (err) {
      setError(err.message || "Nie udało się pobrać wniosków.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchPowodWpisaniaOptions()
      .then((options) => setPowodOptions(sortUsers(options)))
      .catch(() => toast.error("Błąd podczas pobierania powodów wpisania."));
    fetchUsers()
      .then((options) => setUsersOptions(sortUsers(options)))
      .catch(() => toast.error("Błąd podczas pobierania użytkowników."));
  }, [fetchData]);

  // Filtry pamiętane między sesjami (ten sam klucz co w poprzedniej wersji).
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      // brak miejsca - filtry po prostu nie zostaną zapamiętane
    }
  }, [filters]);

  // Skrót "/" ustawia fokus w wyszukiwarce.
  useEffect(() => {
    const onKey = (event) => {
      const tag = (event.target.tagName || "").toLowerCase();
      if (event.key === "/" && !["input", "textarea", "select"].includes(tag)) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  }, []);

  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const debouncedFilters = useDebounced(filters, 250);

  const filteredWnioski = useMemo(
    () => applyFilters(wnioski, debouncedFilters, wezwaniaMode),
    [wnioski, debouncedFilters, wezwaniaMode],
  );

  const displayedWnioski = useMemo(() => {
    const rows = wezwaniaMode ? filteredWnioski.filter(isWezwanie) : filteredWnioski;
    if (!sortBy) return rows;
    return [...rows].sort((a, b) => compareValues(a[sortBy], b[sortBy], sortDir));
  }, [filteredWnioski, wezwaniaMode, sortBy, sortDir]);

  const total = displayedWnioski.length;
  const pageSize = prefs.pageSize;
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  const safePage = Math.min(page, lastPage);
  const pageRows = displayedWnioski.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const uniqueLabel = wezwaniaMode ? "Nr wezwania" : "Nr wniosku";
  const columns = useMemo(
    () =>
      COLUMNS.filter(
        (c) =>
          !prefs.hiddenColumns.includes(c.id) &&
          !(wezwaniaMode && WEZWANIA_HIDDEN.includes(c.id)),
      ),
    [prefs.hiddenColumns, wezwaniaMode],
  );
  const menuColumns = useMemo(
    () => COLUMNS.filter((c) => !(wezwaniaMode && WEZWANIA_HIDDEN.includes(c.id))),
    [wezwaniaMode],
  );

  const activeChips = useMemo(() => describeFilters(filters), [filters]);
  const activeCount = useMemo(
    () => describeFilters({ ...filters, searchTerm: "" }).length,
    [filters],
  );
  const hasFilters = activeChips.length > 0;

  const handleSort = (key) => {
    setPage(0);
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortBy(null);
      setSortDir("asc");
    }
  };

  const inProgressActive = sameSet(filters.status, IN_PROGRESS_STATUSES);
  const toggleInProgress = () =>
    updateFilters({ status: inProgressActive ? [] : IN_PROGRESS_STATUSES });

  const presets = [
    {
      id: "wToku",
      label: "Sprawy w toku",
      hint: "Status: W toku, Do wysłania, Do weryfikacji, Do poprawy, Do poprawy na wniosek UC",
      active: inProgressActive,
      toggle: toggleInProgress,
    },
    {
      id: "pytania",
      label: "Bieżące pytania z urzędu",
      hint: "Wnioski z pytaniem z urzędu lub terminem ustawowym, jeszcze niezakończone",
      active: filters.pokazBiezacePytania,
      toggle: () => updateFilters({ pokazBiezacePytania: !filters.pokazBiezacePytania }),
    },
    {
      id: "bezOdpowiedzi",
      label: "Wezwania bez naszej odpowiedzi",
      hint: "Jest termin odpowiedzi, ale nie ma jeszcze daty naszej odpowiedzi",
      active: filters.pokazBezOdpowiedzi,
      toggle: () => updateFilters({ pokazBezOdpowiedzi: !filters.pokazBezOdpowiedzi }),
    },
  ];

  // ---- Zaznaczanie ----
  const handleSelect = useCallback((row) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id];
      else {
        next[row.id] = {
          id: row.id,
          podpisany: row.podpisany || "",
          uniqueNumber: row.unique_number || "",
          status: row.status || "",
        };
      }
      return next;
    });
  }, []);

  const selectedList = useMemo(() => Object.values(selected), [selected]);
  const pageAllSelected = pageRows.length > 0 && pageRows.every((r) => selected[r.id]);
  const pageSomeSelected = pageRows.some((r) => selected[r.id]) && !pageAllSelected;

  const handleSelectPage = (event) => {
    const { checked } = event.target;
    setSelected((prev) => {
      const next = { ...prev };
      pageRows.forEach((row) => {
        if (checked) {
          next[row.id] = {
            id: row.id,
            podpisany: row.podpisany || "",
            uniqueNumber: row.unique_number || "",
            status: row.status || "",
          };
        } else {
          delete next[row.id];
        }
      });
      return next;
    });
  };

  const handleToggleExpand = useCallback(
    (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
    [],
  );
  const handleEdit = useCallback((id) => navigate(`/rejestrWnioskow/${id}`), [navigate]);

  // ---- Akcje ----
  const handleSaveComment = async () => {
    try {
      setCommentSaving(true);
      await addCommentToMultipleWnioski(selectedList, commentText);
      toast.success("Komentarz dodany do wybranych wniosków.");
      setCommentOpen(false);
      setCommentText("");
      setSelected({});
      fetchData();
    } catch (err) {
      toast.error("Błąd podczas dodawania komentarza.");
    } finally {
      setCommentSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteTarget;
    if (!target) return;
    try {
      const response = await deleteWniosek(target.id);
      if (response.status === 200) {
        setWnioski((prev) => prev.filter((w) => w.id !== target.id));
        setSelected((prev) => {
          const next = { ...prev };
          delete next[target.id];
          return next;
        });
        toast.success("Wniosek został pomyślnie usunięty!");
      } else {
        toast.error(`Nie udało się usunąć wniosku. Status: ${response.status}`);
      }
    } catch (err) {
      console.error("Błąd podczas usuwania wniosku:", err);
      toast.error("Błąd podczas usuwania wniosku.");
    } finally {
      setDeleteTarget(null);
    }
  };

  const exportToXLSX = () => {
    setExportAnchor(null);
    const excludedColumns = [
      "id",
      "data_wpisania",
      "podpisany",
      "data_zakonczenia",
      "sciezkaDoPliku",
      "data_odebrania",
      "weryfikacja",
      "duplikat",
      "history",
      "comments",
    ];
    const exportRows = displayedWnioski.map((row) => {
      const newRow = { ...row };
      excludedColumns.forEach((col) => delete newRow[col]);
      if (newRow.unique_number !== undefined) {
        newRow.znak_wniosku = newRow.unique_number;
        delete newRow.unique_number;
      }
      if (newRow.nadawca_odbiorca !== undefined) {
        newRow.nadawca = newRow.nadawca_odbiorca;
        delete newRow.nadawca_odbiorca;
      }
      return newRow;
    });
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Wnioski");
    XLSX.writeFile(workbook, "Wnioski.xlsx");
    toast.success(`Wyeksportowano ${exportRows.length} wniosków.`);
  };

  // ---- Pola panelu filtrów ----
  const text = (key, label, extra = {}) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      autoComplete="off"
      value={filters[key]}
      onChange={(e) => updateFilters({ [key]: e.target.value })}
      {...extra}
    />
  );

  const single = (key, label, items) => (
    <Autocomplete
      size="small"
      options={items}
      value={filters[key] || null}
      onChange={(_, value) => updateFilters({ [key]: value || "" })}
      noOptionsText="Brak opcji"
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );

  const range = (fromKey, toKey, label) => (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <TextField
          label="Od"
          type="date"
          size="small"
          value={filters[fromKey]}
          onChange={(e) => updateFilters({ [fromKey]: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Do"
          type="date"
          size="small"
          value={filters[toKey]}
          inputProps={{ min: filters[fromKey] || undefined }}
          onChange={(e) => updateFilters({ [toKey]: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      </div>
    </div>
  );

  const checkbox = (key, label) => (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          color="primary"
          checked={filters[key]}
          onChange={(e) => updateFilters({ [key]: e.target.checked })}
        />
      }
      label={label}
    />
  );

  const headerSticky = [
    { left: 0, width: 52 },
    { left: 52, width: 40 },
    { left: 92, width: STICKY_NUMBER_WIDTH },
  ];

  return (
    <>
      <div className={classes.pageHeader}>
        <div>
          <h1 className={classes.title}>{wezwaniaMode ? "Wezwania" : "Rejestr wniosków"}</h1>
          <div className={classes.subtitle}>
            <span>
              <strong style={{ color: "#212121" }}>{total}</strong>{" "}
              {hasFilters
                ? wezwaniaMode ? "wezwań spełnia filtry" : "wniosków spełnia filtry"
                : wezwaniaMode ? "wezwań" : "wniosków w rejestrze"}
            </span>
            <Tooltip title="Odśwież teraz">
              <IconButton size="small" onClick={fetchData} aria-label="Odśwież">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div className={classes.headerActions}>
          <Button
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
          >
            Kolumny
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            Eksport
          </Button>
          {canSeeLists && (
            <Button variant="outlined" startIcon={<ListAltIcon />} onClick={() => navigate("/rejestrLists")}>
              Listy
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => navigate("/nowyWniosek")}
          >
            Dodaj wniosek
          </Button>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.segmented} role="tablist" aria-label="Widok rejestru">
            <button
              type="button"
              role="tab"
              aria-selected={!wezwaniaMode}
              className={`${classes.segment} ${!wezwaniaMode ? classes.segmentActive : ""}`}
              onClick={() => { setWezwaniaMode(false); setPage(0); }}
            >
              Wszystkie wnioski
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={wezwaniaMode}
              className={`${classes.segment} ${wezwaniaMode ? classes.segmentActive : ""}`}
              onClick={() => { setWezwaniaMode(true); setPage(0); }}
            >
              Wezwania
            </button>
          </div>

          <div className={classes.search}>
            <SearchIcon fontSize="small" />
            <InputBase
              inputRef={searchRef}
              className={classes.searchInput}
              placeholder="Szukaj w każdym polu wniosku…"
              value={filters.searchTerm}
              onChange={(e) => updateFilters({ searchTerm: e.target.value })}
              inputProps={{ "aria-label": "Szukaj" }}
            />
            {filters.searchTerm ? (
              <IconButton size="small" onClick={() => updateFilters({ searchTerm: "" })} aria-label="Wyczyść wyszukiwanie">
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : (
              <kbd className={classes.kbd}>/</kbd>
            )}
          </div>

          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant={filtersOpen || activeCount > 0 ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              disableElevation
            >
              Filtry
            </Button>
          </Badge>

          {presets.map((preset) => (
            <Tooltip key={preset.id} title={preset.hint}>
              <Chip
                className={classes.presetChip}
                label={preset.label}
                color={preset.active ? "primary" : "default"}
                variant={preset.active ? "filled" : "outlined"}
                onClick={preset.toggle}
                onDelete={preset.active ? preset.toggle : undefined}
              />
            </Tooltip>
          ))}

          <span className={classes.toolbarGap} />
          {hasFilters && (
            <Button size="small" onClick={clearAllFilters}>
              Wyczyść filtry
            </Button>
          )}
        </div>

        {hasFilters && (
          <div className={classes.activeFilters}>
            {activeChips.map((chip) => (
              <Chip
                key={chip.id}
                size="small"
                label={chip.label}
                onDelete={() => updateFilters(chip.clear)}
                style={{ background: COLORS.primarySoft, color: COLORS.primaryDark }}
              />
            ))}
          </div>
        )}

        {selectedList.length > 0 && (
          <div className={classes.selectionBar}>
            <strong>Zaznaczono: {selectedList.length}</strong>
            <Button size="small" variant="contained" startIcon={<CommentIcon />} onClick={() => setCommentOpen(true)} disableElevation>
              Dodaj komentarz
            </Button>
            <Button size="small" onClick={() => setSelected({})}>
              Odznacz wszystko
            </Button>
          </div>
        )}

        {error && <div className={classes.error}>{error}</div>}

        <div className={classes.tableWrap}>
          {loading && <LinearProgress className={classes.progress} />}
          <Table stickyHeader size="small" style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{ left: headerSticky[0].left, width: headerSticky[0].width, minWidth: headerSticky[0].width }}
                >
                  <Checkbox
                    size="small"
                    color="primary"
                    indeterminate={pageSomeSelected}
                    checked={pageAllSelected}
                    onChange={handleSelectPage}
                    inputProps={{ "aria-label": "Zaznacz stronę" }}
                  />
                </TableCell>
                <TableCell
                  padding="none"
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{ left: headerSticky[1].left, width: headerSticky[1].width, minWidth: headerSticky[1].width }}
                />
                <TableCell
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{ left: headerSticky[2].left, minWidth: headerSticky[2].width, whiteSpace: "nowrap", boxShadow: "inset -1px 0 0 #e0e0e0" }}
                >
                  <TableSortLabel
                    active={sortBy === "unique_number"}
                    direction={sortBy === "unique_number" ? sortDir : "asc"}
                    onClick={() => handleSort("unique_number")}
                  >
                    {uniqueLabel}
                  </TableSortLabel>
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={classes.headCell}
                    style={{ minWidth: column.width, maxWidth: column.noTruncate ? undefined : column.width }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortBy === column.id}
                        direction={sortBy === column.id ? sortDir : "asc"}
                        onClick={() => handleSort(column.id)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                <TableCell className={classes.headCell} style={{ width: 96 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row) => (
                <WniosekRow
                  key={row.id}
                  row={row}
                  columns={columns}
                  selected={Boolean(selected[row.id])}
                  onSelect={handleSelect}
                  expanded={Boolean(expanded[row.id])}
                  onToggle={handleToggleExpand}
                  onEdit={handleEdit}
                  onDelete={setDeleteTarget}
                  isAdmin={isAdmin}
                  wezwaniaMode={wezwaniaMode}
                />
              ))}
            </TableBody>
          </Table>

          {!loading && pageRows.length === 0 && !error && (
            <div className={classes.empty}>
              <SearchOffIcon style={{ fontSize: 44, color: "#bdbdbd" }} />
              <strong style={{ fontSize: 17, color: "#424242" }}>
                {wezwaniaMode ? "Brak wezwań do wyświetlenia" : "Brak wniosków do wyświetlenia"}
              </strong>
              <span>
                {hasFilters
                  ? "Żaden wniosek nie spełnia wybranych filtrów."
                  : "W rejestrze nie ma jeszcze żadnych wpisów."}
              </span>
              {hasFilters && (
                <Button variant="outlined" onClick={clearAllFilters}>
                  Wyczyść filtry
                </Button>
              )}
            </div>
          )}
        </div>

        <TablePagination
          component="div"
          count={total}
          page={safePage}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            prefs.setPageSize(Number(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </div>

      <FilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onClear={clearAllFilters}
        activeCount={activeCount}
        total={total}
        totalLabel={wezwaniaMode ? "Wezwań" : "Wniosków"}
      >
        <FilterCard
          title="Wyszukiwanie"
          count={countFilled(filters.nr_MRN, filters.nr_akt, filters.nadawca, filters.odbiorca)}
        >
          {text("nr_MRN", "Nr MRN")}
          {text("nr_akt", "Nr akt")}
          {text("nadawca", "Nadawca")}
          {text("odbiorca", "Odbiorca")}
        </FilterCard>

        <FilterCard
          title="Status i osoby"
          count={countFilled(
            filters.status,
            filters.podpisany,
            filters.powod_wpisania,
            filters.procedura,
            filters.platnik,
          )}
        >
          <Autocomplete
            multiple
            size="small"
            options={STATUS_OPTIONS}
            value={filters.status}
            onChange={(_, value) => updateFilters({ status: value })}
            limitTags={2}
            disableCloseOnSelect
            noOptionsText="Brak opcji"
            renderInput={(params) => <TextField {...params} label="Status" />}
          />
          {single("podpisany", "Osoba odpowiedzialna", usersOptions.map((o) => o.name))}
          {single("powod_wpisania", "Powód wpisania", powodOptions.map((o) => o.name))}
          {single("procedura", "Procedura", PROCEDURE_OPTIONS)}
          {single("platnik", "Płatnik", PAYER_OPTIONS)}
        </FilterCard>

        <FilterCard
          title="Daty"
          count={countFilled(
            filters.data_wpisania,
            filters.data_wyslania_start || filters.data_wyslania_end,
            filters.data_odebrania_start || filters.data_odebrania_end,
            filters.data_terminu_start || filters.data_terminu_end,
          )}
        >
          {text("data_wpisania", "Data wpisania", {
            type: "date",
            InputLabelProps: { shrink: true },
          })}
          {range("data_wyslania_start", "data_wyslania_end", "Wysłano do UC")}
          {range("data_odebrania_start", "data_odebrania_end", "Odebrano dokumenty z UC")}
          {range("data_terminu_start", "data_terminu_end", "Termin odpowiedzi")}
        </FilterCard>

        <FilterCard
          title="Wezwania i pytania"
          count={countFilled(filters.pokazBiezacePytania, filters.pokazBezOdpowiedzi)}
        >
          {checkbox("pokazBiezacePytania", "Pokaż bieżące pytania z urzędu")}
          {checkbox("pokazBezOdpowiedzi", "Pokaż wezwania bez naszej odpowiedzi")}
        </FilterCard>
      </FilterDrawer>

      <ColumnsMenu
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        columns={menuColumns}
        hiddenColumns={prefs.hiddenColumns}
        onToggle={prefs.toggleColumn}
        onReset={prefs.resetColumns}
      />

      <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
        <MenuItem onClick={exportToXLSX} disabled={total === 0}>
          <ListItemIcon>
            <TableChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Excel – wszystkie wyniki"
            secondary={`${total} ${wezwaniaMode ? "wezwań" : "wniosków"} spełniających filtry`}
          />
        </MenuItem>
      </Menu>

      <Dialog open={commentOpen} onClose={() => !commentSaving && setCommentOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Dodaj komentarz do zaznaczonych wniosków</DialogTitle>
        <DialogContent>
          <p style={{ margin: "0 0 12px", color: COLORS.textMuted, fontSize: 14 }}>
            Komentarz zostanie zapisany w {selectedList.length} wnioskach.
          </p>
          <p style={{ margin: "0 0 16px", fontSize: 14 }}>
            <strong>Numery:</strong> {selectedList.map((w) => w.uniqueNumber).join(", ")}
          </p>
          <TextField
            multiline
            fullWidth
            minRows={4}
            maxRows={8}
            autoFocus
            label="Komentarz"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentOpen(false)} disabled={commentSaving}>
            Anuluj
          </Button>
          <Button
            onClick={handleSaveComment}
            variant="contained"
            disabled={commentSaving || !commentText.trim()}
          >
            Zapisz komentarz
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Usunąć wniosek?</DialogTitle>
        <DialogContent>
          Wniosek <strong>{deleteTarget?.unique_number}</strong> zostanie trwale usunięty. Tej
          operacji nie można cofnąć.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Anuluj</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RejestrWnioskow;
